import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { X, Download, Smartphone, Monitor } from 'lucide-react';

const PWA_PROMPT_DISMISSED_KEY = 'ofs_pwa_prompt_dismissed';

// Helper functions to check device state
const checkIsStandalone = () => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(display-mode: standalone)').matches 
    || window.navigator.standalone 
    || document.referrer.includes('android-app://');
};

const checkIsIOS = () => {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};

export const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const isIOS = checkIsIOS();
  const isStandalone = checkIsStandalone();

  useEffect(() => {
    if (isStandalone) return;

    // Check if prompt was dismissed
    const dismissed = localStorage.getItem(PWA_PROMPT_DISMISSED_KEY);
    const dismissedTime = dismissed ? parseInt(dismissed) : 0;
    const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);

    // Listen for the beforeinstallprompt event (Android/Desktop Chrome)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show prompt if not dismissed in last 7 days
      if (daysSinceDismissed > 7) {
        setTimeout(() => setShowPrompt(true), 3000); // Delay showing prompt
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // For iOS, show custom prompt after delay
    if (isIOS && daysSinceDismissed > 7) {
      setTimeout(() => setShowPrompt(true), 5000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, [isIOS, isStandalone]);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(PWA_PROMPT_DISMISSED_KEY, Date.now().toString());
    setShowPrompt(false);
  };

  // Don't show if already installed or prompt not ready
  if (isStandalone || !showPrompt) return null;

  return (
    <div 
      className="fixed bottom-16 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 z-50 animate-in slide-in-from-bottom-5 duration-300"
      data-testid="pwa-install-prompt"
    >
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-xl border border-slate-700 shadow-2xl p-4">
        <button 
          onClick={handleDismiss}
          className="absolute top-2 right-2 p-1 rounded-full hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
          data-testid="pwa-dismiss-button"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-start gap-4">
          <div className="p-3 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex-shrink-0">
            {isIOS ? (
              <Smartphone className="h-6 w-6 text-white" />
            ) : (
              <Download className="h-6 w-6 text-white" />
            )}
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-white text-sm mb-1">
              Install Option Wizard
            </h3>
            <p className="text-xs text-slate-400 mb-3">
              {isIOS 
                ? 'Add to your home screen for the best experience'
                : 'Install for quick access and offline support'
              }
            </p>

            {isIOS ? (
              <div className="bg-slate-800/50 rounded-lg p-3 text-xs text-slate-300">
                <p className="mb-2">To install:</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Tap the <strong className="text-white">Share</strong> button</li>
                  <li>Scroll down and tap <strong className="text-white">&ldquo;Add to Home Screen&rdquo;</strong></li>
                </ol>
              </div>
            ) : (
              <div className="flex gap-2">
                <Button 
                  onClick={handleInstall}
                  size="sm"
                  className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white text-xs"
                  data-testid="pwa-install-button"
                >
                  <Download className="h-3.5 w-3.5 mr-1.5" />
                  Install App
                </Button>
                <Button 
                  onClick={handleDismiss}
                  variant="ghost"
                  size="sm"
                  className="text-slate-400 hover:text-white text-xs"
                >
                  Maybe Later
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
